/**
 * URL field `formatting_options` (Advanced tab). See ./URL_MANAGEMENT_V2.md.
 */

export type UrlFormattingCase = 'lowercase' | 'uppercase' | 'capitalise' | 'none'

export type UrlFormattingWordSeparator = 'hyphen' | 'underscore' | 'plus' | 'none'

export type UrlFormattingRegexOptions = {
  pattern: string
  replaceWith: string
  global: boolean
  caseInsensitive: boolean
}

export type UrlFieldFormattingOptions = {
  case: UrlFormattingCase
  word_separator: UrlFormattingWordSeparator
  substitute_missing_value: string
  regex: UrlFormattingRegexOptions
  map: Record<string, string>
}

/** Schema slice read/written by URL formatting helpers (may be partial until normalized). */
export type UrlFieldFormattingOptionsSchema = {
  formatting_options?: Partial<UrlFieldFormattingOptions>
}

export type SelectOption<T extends string = string> = {
  value: T
  label: string
  /** Shown in the open menu (right-aligned), e.g. "e.g. my sample title". */
  example?: string
}

export const URL_FORMATTING_MAP_JSON_MAX_LENGTH = 256

export const URL_FORMATTING_CASE_OPTIONS: ReadonlyArray<SelectOption<UrlFormattingCase>> = [
  { value: 'lowercase', label: 'Lowercase', example: 'e.g. my sample title' },
  { value: 'uppercase', label: 'Uppercase', example: 'e.g. MY SAMPLE TITLE' },
  { value: 'capitalise', label: 'Capitalise', example: 'e.g. My Sample Title' },
  // { value: 'none', label: 'None' }
]

export const URL_FORMATTING_WORD_SEPARATOR_OPTIONS: ReadonlyArray<SelectOption<UrlFormattingWordSeparator>> = [
  { value: 'hyphen', label: 'Hyphen' },
  { value: 'underscore', label: 'Underscore' },
  { value: 'plus', label: 'Plus' },
  // { value: 'none', label: 'None' }
]

/** Suggested regex when the user configures the optional Regex section (not applied until pattern is set). */
export const DEFAULT_URL_FORMATTING_REGEX_PATTERN = '[^a-z0-9_-]+'
export const DEFAULT_URL_FORMATTING_REGEX_REPLACE_WITH = '-'

const EMPTY_REGEX: UrlFormattingRegexOptions = {
  pattern: '',
  replaceWith: '',
  global: false,
  caseInsensitive: true
}

/** True when a non-empty regex pattern is configured (backend should skip regex when false). */
export const isUrlFormattingRegexConfigured = (regex: UrlFormattingRegexOptions): boolean =>
  regex.pattern.trim() !== ''

export const DEFAULT_URL_FIELD_FORMATTING_OPTIONS: UrlFieldFormattingOptions = {
  case: 'lowercase',
  word_separator: 'hyphen',
  substitute_missing_value: '',
  regex: { ...EMPTY_REGEX },
  map: {}
}

const isValidCase = (v: unknown): v is UrlFormattingCase =>
  URL_FORMATTING_CASE_OPTIONS.some((o) => o.value === v)

const isValidWordSeparator = (v: unknown): v is UrlFormattingWordSeparator =>
  URL_FORMATTING_WORD_SEPARATOR_OPTIONS.some((o) => o.value === v)

/** Maps legacy enum storage to a free-text fallback value for the UI. */
export const normalizeDefaultEmptyValue = (v: unknown): string => {
  if (typeof v !== 'string') return DEFAULT_URL_FIELD_FORMATTING_OPTIONS.substitute_missing_value
  if (v === 'none') return ''
  if (v === 'dash') return '-'
  if (v === 'underscore') return '_'
  return v
}

/**
 * Ensures `schema.formatting_options` exists and merges partial / legacy values with defaults.
 */
export const normalizeUrlFieldFormattingOptions = (
  raw: Partial<UrlFieldFormattingOptions> | null | undefined
): UrlFieldFormattingOptions => {
  const regexRaw = raw?.regex
  return {
    case: isValidCase(raw?.case) ? raw.case : DEFAULT_URL_FIELD_FORMATTING_OPTIONS.case,
    word_separator: isValidWordSeparator(raw?.word_separator)
      ? raw.word_separator
      : DEFAULT_URL_FIELD_FORMATTING_OPTIONS.word_separator,
    substitute_missing_value: normalizeDefaultEmptyValue(raw?.substitute_missing_value),
    regex: {
      pattern: typeof regexRaw?.pattern === 'string' ? regexRaw.pattern : EMPTY_REGEX.pattern,
      replaceWith:
        typeof regexRaw?.replaceWith === 'string' ? regexRaw.replaceWith : EMPTY_REGEX.replaceWith,
      global: typeof regexRaw?.global === 'boolean' ? regexRaw.global : EMPTY_REGEX.global,
      caseInsensitive:
        typeof regexRaw?.caseInsensitive === 'boolean'
          ? regexRaw.caseInsensitive
          : EMPTY_REGEX.caseInsensitive
    },
    map: mapObjectFromRecord(raw?.map)
  }
}

export const getUrlFieldFormattingOptions = (
  schema: UrlFieldFormattingOptionsSchema
): UrlFieldFormattingOptions => normalizeUrlFieldFormattingOptions(schema.formatting_options)

export const formattingMapToJsonString = (map: Record<string, string>): string => {
  if (Object.keys(map ?? {}).length === 0) {
    return ''
  }
  try {
    return JSON.stringify(map)
  } catch {
    return ''
  }
}

/** Plain object whose every key and value is a string (not an array or primitive). */
export const isStringMapObject = (value: unknown): value is Record<string, string> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  return Object.entries(value as Record<string, unknown>).every(
    ([key, entryValue]) => typeof key === 'string' && typeof entryValue === 'string'
  )
}

/** Parse JSON object literals; supports double-quoted JSON and simple single-quoted objects. */
const parseObjectLiteral = (text: string): unknown => {
  try {
    return JSON.parse(text)
  } catch {
    try {
      const normalized = text.replace(
        /'([^'\\]*(?:\\.[^'\\]*)*)'/g,
        (_, inner: string) => `"${inner.replace(/\\'/g, "'").replace(/"/g, '\\"')}"`
      )
      return JSON.parse(normalized)
    } catch {
      return undefined
    }
  }
}

/**
 * Validates user map JSON: one object, all keys and values must be strings.
 * Empty input is valid (`{}`). Single- and double-quoted strings are accepted.
 */
export const parseFormattingMapJson = (
  json: string
): { map: Record<string, string>; valid: boolean } => {
  const trimmed = json.trim()
  if (!trimmed) {
    return { map: {}, valid: true }
  }

  const parsed = parseObjectLiteral(trimmed)
  if (!isStringMapObject(parsed)) {
    return { map: {}, valid: false }
  }

  return { map: parsed, valid: true }
}

const mapObjectFromRecord = (map: unknown): Record<string, string> =>
  isStringMapObject(map) ? { ...map } : {}

export const commitUrlFieldFormattingOptions = (
  schema: UrlFieldFormattingOptionsSchema,
  next: UrlFieldFormattingOptions
): void => {
  schema.formatting_options = next
}

export const ensureUrlFieldFormattingOptionsOnSchema = (
  schema: UrlFieldFormattingOptionsSchema
): UrlFieldFormattingOptions => {
  const next = normalizeUrlFieldFormattingOptions(schema.formatting_options)
  commitUrlFieldFormattingOptions(schema, next)
  return next
}
